import React, { useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import { LuPencil, LuTrash2, LuUser, LuWallet } from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  setEditEmployee,
  toggleEmployeeModal,
} from "../../features/employees/employeesSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import useConfirmPopup from "../../context/useConfirmPopup";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { EmployeesProps } from "../../types/employees";
import Badge from "../ui/Badge";
import Button from "../ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import {
  EmptyState,
  LoadingState,
  TableEmptyRow,
  TableLoadingRow,
} from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  cardClass,
  cardContentFlushClass,
  mobileCardClass,
  moneyCellClass,
  tableHeadCellClass,
} from "../ui/styles";

type Props = {
  employees: EmployeesProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  handleDeleteEmployee: (id: number) => void;
};

const formatAmount = (value: number | null) =>
  (value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value: string) => moment(value).format("DD-MM-YYYY");

const actionSpinnerClass = cx(
  buttonSpinnerClass,
  "mr-0 border-[#113F67]/30 border-t-[#113F67]"
);

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const actionGridClass: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

const fixedBadge = (isFixed: number) =>
  isFixed === 2 ? (
    <Badge
      variant="none"
      className="whitespace-nowrap bg-green-100 text-green-800 hover:bg-primary/80"
    >
      ફિક્સ છે
    </Badge>
  ) : (
    <Badge
      variant="none"
      className="whitespace-nowrap bg-orange-100 text-orange-800 hover:bg-primary/80"
    >
      ફિક્સ નથી
    </Badge>
  );

const statusBadge = (status: number) =>
  status === 1 ? (
    <Badge
      variant="none"
      className="bg-green-100 text-green-800 hover:bg-green-100"
    >
      સક્રિય
    </Badge>
  ) : (
    <Badge variant="none" className="bg-red-100 text-red-800 hover:bg-red-100">
      નિષ્ક્રિય
    </Badge>
  );

const EmployeeList = ({
  employees,
  pageNumber,
  pageSize,
  isLoading,
  handleDeleteEmployee,
}: Props) => {
  const dispatch = useAppDispatch();
  const confirmDelete = useConfirmPopup();
  const loggingUser = useAppSelector(getLoggingUser);

  const canReadSalaries = hasPermission(
    loggingUser?.permissions,
    permission.salariesRead
  );
  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.employeesUpdate
  );
  const canDelete = hasPermission(
    loggingUser?.permissions,
    permission.employeesDelete
  );

  const actionCount =
    (canReadSalaries ? 1 : 0) + (canUpdate ? 1 : 0) + (canDelete ? 1 : 0);
  const tableColSpan = 10 + (actionCount > 0 ? 1 : 0);

  const [isActionLoading, setIsActionLoading] = useState<number>(0);

  const handleEditEmployee = async (employee: EmployeesProps) => {
    await dispatch(setEditEmployee(employee));
    dispatch(toggleEmployeeModal(true));
  };

  const handleDeleteRow = async (id: number) => {
    const confirmed = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!",
      true
    );

    if (confirmed) {
      setIsActionLoading(id);
      await handleDeleteEmployee(id);
      setIsActionLoading(0);
    }
  };

  return (
    <>
      <div className={cx(cardClass, "max-sm:hidden")}>
        <div className={cardContentFlushClass}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={tableHeadCellClass}>ક્રમ</TableHead>
                  <TableHead className={tableHeadCellClass}>ફિક્સ</TableHead>
                  <TableHead className={tableHeadCellClass}>
                    કામદાર નું નામ
                  </TableHead>
                  <TableHead className={tableHeadCellClass}>
                    કાર્ડ નંબર
                  </TableHead>
                  <TableHead className={tableHeadCellClass}>ફોન નંબર</TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    પગાર રેટ
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    બાકી પગાર
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    બાકી ઉપાડ
                  </TableHead>
                  <TableHead className={tableHeadCellClass}>સ્ટેટ્સ</TableHead>
                  <TableHead className={tableHeadCellClass}>તારીખ</TableHead>
                  {actionCount > 0 && (
                    <TableHead className={cx(tableHeadCellClass, "text-right")}>
                      એકશન્સ બટન
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>

              {isLoading ? (
                <TableLoadingRow colSpan={tableColSpan} />
              ) : employees.length === 0 ? (
                <TableEmptyRow
                  colSpan={tableColSpan}
                  label="કામદાર ની વિગતો મળી નથી"
                />
              ) : (
                <TableBody>
                  {employees.map((employee, index) => (
                    <TableRow key={employee.id}>
                      <TableCell className="font-medium">
                        {(pageNumber - 1) * pageSize + index + 1}
                      </TableCell>

                      <TableCell>
                        <div className="flex gap-1">
                          {fixedBadge(employee.is_fixed)}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2">
                          <LuUser className="h-4 w-4 flex-shrink-0 text-gray-400" />
                          <div>
                            <div className="font-medium">
                              {employee.name || "-"}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {employee.id}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>{employee.employee_code || "-"}</TableCell>

                      <TableCell>
                        <div className="font-medium">
                          {employee.phone || "-"}
                        </div>
                      </TableCell>

                      <TableCell className={moneyCellClass}>
                        {formatAmount(employee.amount)}
                      </TableCell>

                      <TableCell className={moneyCellClass}>
                        {formatAmount(employee.remaining_payment)}
                      </TableCell>

                      <TableCell className={moneyCellClass}>
                        {formatAmount(employee.salary)}
                      </TableCell>

                      <TableCell>{statusBadge(employee.status)}</TableCell>

                      <TableCell className="text-sm text-gray-500">
                        {formatDate(employee.created_at)}
                      </TableCell>

                      {actionCount > 0 && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {isActionLoading === employee.id ? (
                              <div className={actionSpinnerClass}></div>
                            ) : (
                              <>
                                {canReadSalaries && (
                                  <Link
                                    to={route.salaries.replace(
                                      ":id",
                                      employee.id as unknown as string
                                    )}
                                  >
                                    <Button
                                      variant="ghost"
                                      size="iconSm"
                                      title="પગાર"
                                    >
                                      <LuWallet className="h-4 w-4" />
                                    </Button>
                                  </Link>
                                )}

                                {canUpdate && (
                                  <Button
                                    variant="ghost"
                                    size="iconSm"
                                    title="સંપાદિત કરો"
                                    onClick={() => handleEditEmployee(employee)}
                                  >
                                    <LuPencil className="h-4 w-4" />
                                  </Button>
                                )}

                                {canDelete && (
                                  <Button
                                    variant="ghost"
                                    size="iconSm"
                                    title="કાઢી નાખો"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => handleDeleteRow(employee.id)}
                                  >
                                    <LuTrash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:hidden">
        {isLoading ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : employees.length === 0 ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <EmptyState label="કામદાર ની વિગતો મળી નથી" />
          </div>
        ) : (
          employees.map((employee, index) => (
            <div key={employee.id} className={mobileCardClass}>
              <div className="flex items-start justify-between gap-3 border-b p-4">
                <div className="flex min-w-0 items-center gap-2">
                  <LuUser className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  <div className="min-w-0">
                    <div className="font-medium break-words">
                      {employee.name || "-"}
                    </div>
                    <div className="text-sm text-gray-500">
                      ID: {employee.id}
                    </div>
                  </div>
                </div>

                {statusBadge(employee.status)}
              </div>

              <div className="space-y-3 p-4 text-sm">
                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ક્રમ</span>
                  <span className="font-medium">
                    {(pageNumber - 1) * pageSize + index + 1}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ફિક્સ</span>
                  <div className="flex min-w-0 flex-wrap justify-end gap-1">
                    {fixedBadge(employee.is_fixed)}
                  </div>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>કાર્ડ નંબર</span>
                  <span className="min-w-0 text-right break-words">
                    {employee.employee_code || "-"}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ફોન નંબર</span>
                  <div className="min-w-0 text-right break-words">
                    <div className="font-medium">{employee.phone || "-"}</div>
                  </div>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>પગાર રેટ</span>
                  <span className={moneyCellClass}>
                    {formatAmount(employee.amount)}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>બાકી પગાર</span>
                  <span className={moneyCellClass}>
                    {formatAmount(employee.remaining_payment)}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>બાકી ઉપાડ</span>
                  <span className={moneyCellClass}>
                    {formatAmount(employee.salary)}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>તારીખ</span>
                  <span className="text-right text-gray-500">
                    {formatDate(employee.created_at)}
                  </span>
                </div>
              </div>

              {actionCount > 0 && (
                <div
                  className={cx(
                    "grid gap-2 border-t p-4",
                    actionGridClass[actionCount]
                  )}
                >
                  {isActionLoading === employee.id ? (
                    <div className="col-span-full flex h-10 items-center justify-center">
                      <div className={actionSpinnerClass}></div>
                    </div>
                  ) : (
                    <>
                      {canReadSalaries && (
                        <Link
                          to={route.salaries.replace(
                            ":id",
                            employee.id as unknown as string
                          )}
                        >
                          <Button
                            variant="outline"
                            className="w-full"
                            title="પગાર"
                          >
                            <LuWallet className="h-4 w-4" />
                          </Button>
                        </Link>
                      )}

                      {canUpdate && (
                        <Button
                          variant="outline"
                          title="સંપાદિત કરો"
                          onClick={() => handleEditEmployee(employee)}
                        >
                          <LuPencil className="h-4 w-4" />
                        </Button>
                      )}

                      {canDelete && (
                        <Button
                          variant="outline"
                          title="કાઢી નાખો"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteRow(employee.id)}
                        >
                          <LuTrash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
};
export default EmployeeList;
